import { Box, Divider, Typography } from "@material-ui/core";
import { forwardRef } from "react";
import type { EpocusReferral } from "../../../../global-providers/rxdb-provider/collections/epocus-referral";
import type { EpocusReferralOutcome } from "../../../../global-providers/rxdb-provider/collections/epocus-referral-outcome";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { HARDCODED_RECEIVERS } from "../_lib/hardcoded-receivers";

const URGENCY_LABELS: Record<string, string> = {
  routine: "Routine",
  urgent: "Urgent",
  emergency: "Emergency",
};

const URGENCY_COLORS: Record<string, string> = {
  routine: "#6b7280",
  urgent: "#d97706",
  emergency: "#dc2626",
};

const DIAGNOSIS_LABELS: Record<string, string> = {
  yes: "Yes — diagnosis confirmed",
  no: "No — not confirmed",
  unable: "Unable to determine",
};

const ACTION_LABELS: Record<string, string> = {
  admitted: "Admitted",
  treatedDischarged: "Treated and discharged",
  referredOnward: "Referred onward",
  noAction: "No action required",
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const Field: FC<{ label: string; value: string; color?: string }> = ({
  label,
  value,
  color,
}) => (
  <Box style={{ marginBottom: 12 }}>
    <Typography
      style={{
        fontSize: rem(11),
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase" as const,
        color: EPOCUS_TEXT_SECONDARY,
        marginBottom: 2,
      }}
    >
      {label}
    </Typography>
    <Typography
      style={{
        fontSize: rem(14),
        color: color ?? EPOCUS_TEXT_PRIMARY,
        fontWeight: color ? 700 : 400,
      }}
    >
      {value}
    </Typography>
  </Box>
);

interface ReferralPrintProps {
  referral: EpocusReferral;
  outcome: EpocusReferralOutcome | null;
}

export const ReferralPrint = forwardRef<HTMLDivElement, ReferralPrintProps>(
  ({ referral, outcome }, ref) => {
    const sender = HARDCODED_RECEIVERS.find(
      (r) => r.id === referral.referringUserId,
    );
    const receiver = HARDCODED_RECEIVERS.find(
      (r) => r.id === referral.receivingUserId,
    );

    return (
      <div ref={ref}>
        <style>{`
          @media print {
            @page { size: A4; margin: 20mm; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        `}</style>

        <Box style={{ fontFamily: "sans-serif", color: EPOCUS_TEXT_PRIMARY }}>
          {/* Header */}
          <Box
            style={{
              borderBottom: `2px solid ${EPOCUS_PRIMARY}`,
              paddingBottom: 16,
              marginBottom: 24,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            <Box>
              <Typography
                style={{
                  fontSize: rem(22),
                  fontWeight: 800,
                  color: EPOCUS_PRIMARY,
                  lineHeight: 1.1,
                }}
              >
                ePocus
              </Typography>
              <Typography
                style={{
                  fontSize: rem(13),
                  color: EPOCUS_TEXT_SECONDARY,
                  marginTop: 2,
                }}
              >
                Clinical Referral Document
              </Typography>
            </Box>
            <Typography
              style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
            >
              Printed {formatDate(new Date().toISOString())}
            </Typography>
          </Box>

          {/* Referral details */}
          <Box style={{ marginBottom: 24 }}>
            <Typography
              style={{
                fontSize: rem(16),
                fontWeight: 700,
                color: EPOCUS_TEXT_PRIMARY,
                marginBottom: 16,
              }}
            >
              Referral Details
            </Typography>
            <Field
              label="Urgency"
              value={URGENCY_LABELS[referral.urgency] ?? referral.urgency}
              color={URGENCY_COLORS[referral.urgency]}
            />
            <Field label="Clinical Reason" value={referral.clinicalReason} />
            <Field
              label="Referring Clinician"
              value={
                sender
                  ? `${sender.name} · ${sender.role}, ${sender.facility}`
                  : referral.referringUserId
              }
            />
            <Field
              label="Receiving Clinician"
              value={
                receiver
                  ? `${receiver.name} · ${receiver.role}, ${receiver.facility}`
                  : referral.receivingUserId
              }
            />
            <Field label="Sent" value={formatDate(referral.sentAt)} />
            {referral.acknowledgedAt && (
              <Field
                label="Acknowledged"
                value={formatDate(referral.acknowledgedAt)}
              />
            )}
          </Box>

          {/* Outcome */}
          {outcome && (
            <>
              <Divider
                style={{ borderColor: EPOCUS_BORDER, marginBottom: 24 }}
              />
              <Box>
                <Typography
                  style={{
                    fontSize: rem(16),
                    fontWeight: 700,
                    color: EPOCUS_TEXT_PRIMARY,
                    marginBottom: 16,
                  }}
                >
                  Outcome
                </Typography>
                <Field label="Date Seen" value={outcome.dateSeen} />
                <Field
                  label="Diagnosis Confirmed"
                  value={
                    DIAGNOSIS_LABELS[outcome.diagnosisConfirmed] ??
                    outcome.diagnosisConfirmed
                  }
                />
                <Field
                  label="Action Taken"
                  value={
                    ACTION_LABELS[outcome.actionTaken] ?? outcome.actionTaken
                  }
                />
                {outcome.clinicalNotes && (
                  <Field label="Clinical Notes" value={outcome.clinicalNotes} />
                )}
                <Field
                  label="Outcome Recorded"
                  value={formatDate(outcome.createdAt)}
                />
              </Box>
            </>
          )}

          {/* Footer */}
          <Box
            style={{
              marginTop: 40,
              paddingTop: 12,
              borderTop: `1px solid ${EPOCUS_BORDER}`,
            }}
          >
            <Typography
              style={{ fontSize: rem(11), color: EPOCUS_TEXT_SECONDARY }}
            >
              This document was generated by ePocus and is intended for clinical
              use only. Please handle in accordance with your facility's data
              protection policy.
            </Typography>
          </Box>
        </Box>
      </div>
    );
  },
);
ReferralPrint.displayName = "ReferralPrint";
